import {
  Heart,
  Sparkles,
  ChevronLeft,
  Edit2,
  AlertCircle,
  SlidersHorizontal,
  Trash2,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Images,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AddToCartButton from "./add-remove-from-cart";
import BaseApi from "../../services/base-api";
import VariationModal from "./variation";
import { Product, ProductVariation } from "../../types/Product";
import { useAuth } from "../../context/AuthContext";
import { ImageWithFallback } from "../../constants/image-placeholder";
import { useToast } from "../../context/ToastContext";
import { FAVORITE } from "../../constants/colors";
import { useTranslation } from "react-i18next";
import Price from "../Currency/Price";
import ProductRating from "./rating";
import LoadingComponent from "../LoadingComponent";
import { hasPermission, PERMISSIONS } from "../../utils/permissions";
import ImageUploader from "../ImageUploader";
import { decodeHTML } from "../../utils/textFormatters";

const DEFAULT_PRODUCT_IMAGE =
  "https://afcogroup.in/wp-content/uploads/2022/11/placeholder-24.png";

const ProductDetails: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const { isAuthenticated, user, permissions } = useAuth();
  const navigate = useNavigate();
  const { success, err } = useToast();
  const { t, i18n } = useTranslation();
  const showRating = true;

  const canEditProduct = hasPermission(permissions, PERMISSIONS.EDIT_PRODUCTS);

  const [product, setProduct] = useState<Product>({} as Product);
  const [loading, setLoading] = useState(true);
  const [showVariationModal, setShowVariationModal] = useState(false);
  const [editingVariation, setEditingVariation] =
    useState<ProductVariation | null>(null);

  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [togglingFavorite, setTogglingFavorite] = useState(false);

  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameSaving, setNameSaving] = useState(false);
  const [editedName, setEditedName] = useState({ en: "", ar: "" });
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [descriptionSaving, setDescriptionSaving] = useState(false);
  const [editedDescription, setEditedDescription] = useState({
    en: "",
    ar: "",
  });

  // Variation states
  const [variations, setVariations] = useState<ProductVariation[]>([]);
  const [selectedAttributes, setSelectedAttributes] = useState<
    Record<string, string>
  >({});
  const [selectedVariation, setSelectedVariation] =
    useState<ProductVariation | null>(null);
  const canEditProductName = isAuthenticated && user?.role !== "customer";

  const [showImageModal, setShowImageModal] = useState(false);
  const [editImages, setEditImages] = useState<string[]>([]);
  const [imageSaving, setImageSaving] = useState(false);

  const stockStatuses = [
    {
      value: "in_stock",
      label: t("In Stock"),
      color: "bg-green-100 text-green-800",
      icon: CheckCircle,
    },
    {
      value: "low_stock",
      label: t("Low Stock"),
      color: "bg-yellow-100 text-yellow-800",
      icon: AlertTriangle,
    },
    {
      value: "out_of_stock",
      label: t("Out of Stock"),
      color: "bg-red-100 text-red-800",
      icon: XCircle,
    },
  ];

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  useEffect(() => {
    if (variations.length > 0 && Object.keys(selectedAttributes).length > 0) {
      findMatchingVariation();
    }
  }, [selectedAttributes, variations]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const res = await BaseApi.get(`/products/${productId}?isActive=true`);
      if (!res.success) throw new Error("Failed to fetch product");

      setProduct(res.data);
      setIsFavorite(!!res.data.isFavorite);
      setVariations(res.data.variations || []);

      if (res.data.variations?.length) {
        const firstVariation = res.data.variations[0];
        const hasSelectedAttributes =
          Object.keys(selectedAttributes).length > 0;

        if (!hasSelectedAttributes) {
          setSelectedAttributes(firstVariation.attributes || {});
          setSelectedVariation(firstVariation);
        } else {
          const matchingVariation = res.data.variations.find((variation: any) =>
            Object.keys(selectedAttributes).every(
              (key) => variation.attributes?.[key] === selectedAttributes[key],
            ),
          );

          if (matchingVariation) {
            setSelectedVariation(matchingVariation);
          } else {
            setSelectedAttributes(firstVariation.attributes || {});
            setSelectedVariation(firstVariation);
          }
        }
      } else if (res.data.colors?.length) {
        // Initialize color for non-variation products
        setSelectedColor(res.data.colors[0]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const findMatchingVariation = () => {
    const match = variations.find((variation) => {
      return Object.keys(selectedAttributes).every(
        (key) => variation.attributes[key] === selectedAttributes[key],
      );
    });
    setSelectedVariation(match || null);

    // Update images if variation has specific images
    if (match?.images?.length) {
      setSelectedImageIndex(0);
    }
  };

  const handleAttributeChange = (type: string, value: string) => {
    setSelectedAttributes((prev) => ({
      ...prev,
      [type]: value,
    }));

    // Reset image index when variation changes
    setSelectedImageIndex(0);
  };

  const handleSaveVariation = async (
    variationsData: any[],
    isEditing: boolean,
  ) => {
    try {
      for (const variationData of variationsData) {
        const endpoint = isEditing
          ? `/products/${productId}/variations/${variationData.id}`
          : `/products/${productId}/variations`;
        const method = isEditing ? "put" : "post";
        const res = await BaseApi[method](endpoint, variationData);
        if (!res.success)
          throw new Error(res.message || "Failed to save variation");
      }
      success(
        isEditing
          ? t("Variation updated successfully")
          : variationsData.length > 1
            ? `${variationsData.length} ${t("variations added successfully")}`
            : t("Variation added successfully"),
      );
      await fetchProduct();
      setShowVariationModal(false);
      setEditingVariation(null);
    } catch (error) {
      console.error("Error saving variation:", error);
      throw error;
    }
  };

  const handleDeleteVariation = async (variationId: string) => {
    // if (!confirm(t("Are you sure you want to delete this variation?"))) return;

    try {
      const res = await BaseApi.delete(
        `/products/${productId}/variations/${variationId}`,
      );

      if (res.success) {
        success(t("Variation deleted successfully"));
        await fetchProduct();
      }
    } catch (error) {
      console.error("Error deleting variation:", error);
      // err(t("Failed to delete variation"));
    }
  };

  const toggleFavorite = async (id: string) => {
    if (togglingFavorite) return;

    try {
      setTogglingFavorite(true);

      const res = await BaseApi.post("/favorites/toggle", { productId: id });

      if (res.success) {
        setIsFavorite(!isFavorite);
        success(
          `${
            !isFavorite
              ? t("Added to favorites Successfully")
              : t("Removed from favorites Successfully")
          }`,
        );

        window.dispatchEvent(
          new CustomEvent("wishlistUpdated", {
            detail: {
              action: !isFavorite ? "add" : "remove",
              productId: id,
            },
          }),
        );
      } else {
        err(
          `Failed to ${
            !isFavorite ? "Add to" : "Remove from"
          } favorites. Please try again`,
        );
      }
    } catch (err) {
      console.error("Favorite toggle failed", err);
    } finally {
      setTimeout(() => setTogglingFavorite(false), 300);
    }
  };

  const getDisplayProductName = () => {
    return (
      (i18n.language === "ar" ? product.name?.ar : product.name?.en) ||
      product.name?.en ||
      product.name?.ar ||
      t("Product Name")
    );
  };

  const startEditingName = () => {
    if (!canEditProductName) return;
    setEditedName({
      en: product.name?.en || "",
      ar: product.name?.ar || "",
    });
    setIsEditingName(true);
  };

  const startEditingDescription = () => {
    if (!canEditProductName) return;
    setEditedDescription({
      en: decodeHTML(product.description?.en || ""),
      ar: decodeHTML(product.description?.ar || ""),
    });
    setIsEditingDescription(true);
  };

  const handleSaveProductName = async () => {
    if (!productId) return;

    const englishName = editedName.en.trim();
    const arabicName = editedName.ar.trim();
    if (!englishName || !arabicName) {
      err(t("Both English and Arabic names are required"));
      return;
    }

    const updatedName = {
      en: englishName,
      ar: arabicName,
    };

    try {
      setNameSaving(true);
      const res = await BaseApi.put(`/products/${productId}`, {
        name: updatedName,
      });

      if (!res?.success) {
        throw new Error("Failed to update product name");
      }

      setProduct((prev) => ({
        ...prev,
        name: res?.data?.name || updatedName,
      }));
      setIsEditingName(false);
      success(t("Product name updated successfully"));
    } catch (error) {
      console.error("Error updating product name:", error);
      err(t("Failed to update product name"));
    } finally {
      setNameSaving(false);
    }
  };

  const handleSaveProductDescription = async () => {
    if (!productId) return;

    const englishDescription = editedDescription.en.trim();
    const arabicDescription = editedDescription.ar.trim();
    if (!englishDescription || !arabicDescription) {
      err(t("Both English and Arabic descriptions are required"));
      return;
    }

    const updatedDescription = {
      en: englishDescription,
      ar: arabicDescription,
    };

    try {
      setDescriptionSaving(true);
      const res = await BaseApi.put(`/products/${productId}`, {
        description: updatedDescription,
      });

      if (!res?.success) {
        throw new Error("Failed to update product description");
      }

      setProduct((prev) => ({
        ...prev,
        description: res?.data?.description || updatedDescription,
      }));
      setIsEditingDescription(false);
      success(t("Product description updated successfully"));
    } catch (error) {
      console.error("Error updating product description:", error);
      err(t("Failed to update product description"));
    } finally {
      setDescriptionSaving(false);
    }
  };

  // Get current price based on variation
  const getCurrentPrice = () => {
    if (selectedVariation) {
      return selectedVariation.price !== null
        ? selectedVariation.price
        : product.price + selectedVariation.priceAdjustment;
    }
    return product.price;
  };

  // Get current stock based on variation
  const getCurrentStock = () => {
    if (selectedVariation) {
      return selectedVariation.stock;
    }
    return product.stock;
  };

  // Get images with fallback
  const images = selectedVariation?.images?.length
    ? selectedVariation.images
    : product.images?.length
      ? product.images
      : [DEFAULT_PRODUCT_IMAGE];

  if (loading) {
    return <LoadingComponent title={t("Products")} />;
  }

  if (!product) {
    return (
      <LoadingComponent
        title=""
        message={t("Product not found")}
        error={true}
      />
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-600 text-lg">
        {t("Product not found.")}
      </div>
    );
  }

  const getStockStatusColor = (status: string) => {
    return (
      stockStatuses.find((s) => s.value === status)?.color ||
      "bg-gray-100 text-gray-800"
    );
  };

  const isOutOfStock = getCurrentStock() === 0;
  const availableVariationTypes = Array.from(
    new Set(
      (product.variations || [])
        .flatMap((v) => Object.keys(v.attributes || {}))
        .filter((type) =>
          product.variations.some((v) => v.attributes?.[type] !== null),
        ),
    ),
  );
  const hasVariations = availableVariationTypes.length > 0;
  const hasSelectedAllVariationTypes = availableVariationTypes.every((type) =>
    Boolean(selectedAttributes[type]),
  );
  const shouldDisableAddToCart =
    isOutOfStock ||
    (hasVariations && (!hasSelectedAllVariationTypes || !selectedVariation));

  const rawDescription =
    i18n.language === "ar" ? product.description?.ar : product.description?.en;

  const finalDescription = rawDescription ? decodeHTML(rawDescription) : "";

  return (
    <div className="min-h-screen bg-[#f2e6db] py-4 sm:py-8 px-4">
      <header className="bg-transparent shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              onClick={() => navigate(-1)}
            >
              <ChevronLeft size={24} />
            </button>
            <h1 className="text-lg font-semibold truncate">
              {getDisplayProductName()}
            </h1>
          </div>

          <div className="flex gap-2">
            {canEditProduct && user?.role === "admin" && (
              <button
                onClick={() => {
                  setEditImages(product.images || []);
                  setShowImageModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <Images size={18} />
                <span className="hidden sm:inline">
                  {t(product.images.length ? "Update Images" : "Add Images")}
                </span>
              </button>
            )}
            {canEditProduct && user?.role === "admin" && (
              <button
                onClick={() => {
                  setEditingVariation(null);
                  setShowVariationModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <SlidersHorizontal size={18} />
                <span className="hidden sm:inline">{t("Add Variation")}</span>
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4 lg:p-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* LEFT SIDE IMAGES */}
          <div className="space-y-4">
            <div className="relative bg-white rounded-lg overflow-hidden shadow-md">
              {/* {variationsLoading ? (
                <div className="w-full h-[400px] lg:h-[600px] bg-gray-200 animate-pulse flex items-center justify-center">
                  <div className="text-gray-400">Loading...</div>
                </div>
              ) : ( */}
              <>
                <ImageWithFallback
                  src={images[selectedImageIndex]}
                  alt={product.name?.en || product.name?.ar || "Product"}
                  className="w-full h-[400px] lg:h-[600px] object-cover"
                />

                {product.badge && (
                  <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {product.badge}
                  </div>
                )}

                {isOutOfStock && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <div className="bg-white px-6 py-3 rounded-lg">
                      <span className="text-xl font-bold text-red-600">
                        {t("Out of Stock")}
                      </span>
                    </div>
                  </div>
                )}
              </>
              {/* )} */}
            </div>

            {images.length > 1 && (
              <div className="grid grid-cols-4 gap-3">
                {images.map((img: string, idx: number) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImageIndex(idx)}
                    className={`relative bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all ${
                      selectedImageIndex === idx
                        ? "ring-2 ring-blue-500 scale-105"
                        : ""
                    }`}
                  >
                    <ImageWithFallback
                      src={img}
                      alt={`${product.name?.en || product.name?.ar || "Product"} - ${idx + 1}`}
                      className="w-full h-20 lg:h-24 object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT SIDE PRODUCT DETAILS */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg p-6 shadow-md space-y-4">
              {!isEditingName ? (
                <div className="flex items-center gap-2">
                  {canEditProductName && (
                    <button
                      onClick={startEditingName}
                      className="p-2 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                      aria-label={t("Edit Product Name")}
                      title={t("Edit Product Name")}
                    >
                      <Edit2 size={18} />
                    </button>
                  )}
                  <h2 className="text-2xl lg:text-3xl font-bold text-gray-900">
                    {getDisplayProductName()}
                  </h2>
                </div>
              ) : (
                <div className="space-y-3">
                  <input
                    type="text"
                    value={editedName.en}
                    onChange={(e) =>
                      setEditedName((prev) => ({
                        ...prev,
                        en: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none transition-all"
                    placeholder={t("English Name")}
                    dir="ltr"
                  />
                  <input
                    type="text"
                    value={editedName.ar}
                    onChange={(e) =>
                      setEditedName((prev) => ({
                        ...prev,
                        ar: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none transition-all"
                    placeholder={t("Arabic Name")}
                    dir="rtl"
                  />
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleSaveProductName}
                      disabled={nameSaving}
                      className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-700 disabled:opacity-60 transition-colors"
                    >
                      {nameSaving ? t("Saving...") : t("Save")}
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingName(false);
                        setEditedName({ en: "", ar: "" });
                      }}
                      disabled={nameSaving}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      {t("Cancel")}
                    </button>
                  </div>
                </div>
              )}

              {!isEditingDescription ? (
                <div className="space-y-3">
                  {canEditProductName && (
                    <div className="flex justify-end">
                      <button
                        onClick={startEditingDescription}
                        className="p-2 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                        aria-label={t("Edit Product Description")}
                        title={t("Edit Product Description")}
                      >
                        <Edit2 size={18} />
                      </button>
                    </div>
                  )}
                  <div
                    className="text-gray-600 leading-relaxed"
                    dangerouslySetInnerHTML={{
                      __html: finalDescription || t("No description available"),
                    }}
                  />
                </div>
              ) : (
                <div className="space-y-3">
                  <textarea
                    value={editedDescription.en}
                    onChange={(e) =>
                      setEditedDescription((prev) => ({
                        ...prev,
                        en: e.target.value,
                      }))
                    }
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none transition-all"
                    placeholder={t("English Description")}
                    dir="ltr"
                  />
                  <textarea
                    value={editedDescription.ar}
                    onChange={(e) =>
                      setEditedDescription((prev) => ({
                        ...prev,
                        ar: e.target.value,
                      }))
                    }
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none transition-all"
                    placeholder={t("Arabic Description")}
                    dir="rtl"
                  />
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleSaveProductDescription}
                      disabled={descriptionSaving}
                      className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-700 disabled:opacity-60 transition-colors"
                    >
                      {descriptionSaving ? t("Saving...") : t("Save")}
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingDescription(false);
                        setEditedDescription({ en: "", ar: "" });
                      }}
                      disabled={descriptionSaving}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      {t("Cancel")}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* VARIATION SELECTORS */}
            {hasVariations &&
              availableVariationTypes.map((type) => {
                const options = Array.from(
                  new Set(
                    product.variations
                      .map((v) => v.attributes?.[type])
                      .filter(Boolean),
                  ),
                );

                return (
                  <div key={type} className="bg-white rounded-lg p-6 shadow-md">
                    <h3 className="text-lg font-semibold mb-4 capitalize">
                      Select {type}
                    </h3>

                    <div className="flex gap-3 flex-wrap">
                      {options.map((value) => {
                        const matchingVariation = product.variations.find(
                          (v) => v.attributes?.[type] === value && v.stock > 0,
                        );

                        const isAvailable = !!matchingVariation;
                        const isSelected = selectedAttributes[type] === value;

                        return (
                          <button
                            key={value}
                            onClick={() =>
                              isAvailable && handleAttributeChange(type, value)
                            }
                            disabled={!isAvailable}
                            className={`px-4 py-2 rounded-lg border-2 transition-all font-medium ${
                              isSelected
                                ? "border-[#0d223f] bg-[#0d223f] text-white shadow-md"
                                : isAvailable
                                  ? "border-gray-300 hover:border-gray-400"
                                  : "border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed"
                            }`}
                          >
                            <span
                              className={!isAvailable ? "line-through" : ""}
                            >
                              {value}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

            {/* VARIATION (Admin Only) */}
            {isAuthenticated &&
              user?.role === "admin" &&
              variations.length > 0 && (
                <div className="bg-white rounded-lg p-6 shadow-md">
                  <h3 className="text-lg font-semibold mb-4">
                    {t("Manage Variations")}
                  </h3>
                  <div className="space-y-3">
                    {variations.map((variation) => (
                      <div
                        key={variation.id}
                        className="flex items-center justify-between p-4 border-2 border-gray-200 rounded-lg hover:border-black transition-all"
                      >
                        <div className="flex-1">
                          <div className="font-medium flex items-center gap-2 flex-wrap">
                            {Object.entries(variation.attributes)
                              .filter(([key, value]) => value !== null)
                              .map(([key, value], index) => (
                                <span
                                  key={key}
                                  className="flex items-center gap-1"
                                >
                                  {index > 0 && (
                                    <span className="text-gray-400">•</span>
                                  )}
                                  <span className="capitalize">{key}:</span>
                                  {key.toLowerCase() === "color" ? (
                                    <span className="flex items-center gap-1.5">
                                      <span
                                        className="w-5 h-5 rounded-full border-2 border-gray-300 inline-block"
                                        style={{
                                          backgroundColor:
                                            value.toLowerCase() === "white"
                                              ? "#FFFFFF"
                                              : value.toLowerCase() === "black"
                                                ? "#000000"
                                                : value.toLowerCase() === "red"
                                                  ? "#EF4444"
                                                  : value.toLowerCase() ===
                                                      "blue"
                                                    ? "#0d223f"
                                                    : value.toLowerCase() ===
                                                        "green"
                                                      ? "#10B981"
                                                      : value.toLowerCase() ===
                                                          "yellow"
                                                        ? "#F59E0B"
                                                        : value.toLowerCase() ===
                                                            "purple"
                                                          ? "#A855F7"
                                                          : value.toLowerCase() ===
                                                              "pink"
                                                            ? "#EC4899"
                                                            : value.toLowerCase() ===
                                                                "orange"
                                                              ? "#F97316"
                                                              : value.toLowerCase() ===
                                                                  "gray"
                                                                ? "#6B7280"
                                                                : value.toLowerCase() ===
                                                                    "navy"
                                                                  ? "#1E3A8A"
                                                                  : value.toLowerCase() ===
                                                                      "teal"
                                                                    ? "#14B8A6"
                                                                    : value.toLowerCase() ===
                                                                        "brown"
                                                                      ? "#92400E"
                                                                      : value.toLowerCase() ===
                                                                          "beige"
                                                                        ? "#D4C5B9"
                                                                        : value.toLowerCase() ===
                                                                            "maroon"
                                                                          ? "#7F1D1D"
                                                                          : value.toLowerCase() ===
                                                                              "olive"
                                                                            ? "#84CC16"
                                                                            : value.toLowerCase(),
                                        }}
                                      />
                                      <span>{value}</span>
                                    </span>
                                  ) : (
                                    <span>{value}</span>
                                  )}
                                </span>
                              ))}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            Stock: {variation.stock} | Price:{" "}
                            {variation.price !== null
                              ? `${variation.price}`
                              : `Base + ${variation.priceAdjustment}`}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setEditingVariation(variation);
                              setShowVariationModal(true);
                            }}
                            className="px-3 py-2 bg-black text-white rounded-lg hover:bg-black text-sm"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            onClick={() => handleDeleteVariation(variation.id)}
                            className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm flex items-center gap-1"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {/* COLOR SELECTOR (for non-variation products) */}
            {!product.variations?.length && product.colors?.length > 0 && (
              <div className="bg-white rounded-lg p-6 shadow-md">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Sparkles size={20} className="text-purple-500" />
                  {t("Select Your Color")}
                </h3>
                <div className="flex gap-3 flex-wrap">
                  {product.colors.map((color: string, i: number) => (
                    <button
                      key={i}
                      onClick={() => setSelectedColor(color)}
                      className={`w-12 h-12 rounded-full border-2 transition-all hover:scale-110 ${
                        selectedColor === color
                          ? "border-gray-900 scale-110"
                          : "border-gray-300"
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
            )}
            {/* STOCK WARNING */}
            {!!(
              product.variations?.length &&
              selectedVariation &&
              selectedVariation.stock < 5 &&
              selectedVariation.stock > 0
            ) && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-center gap-2">
                <AlertCircle size={20} className="text-orange-600" />
                <span className="text-orange-800 font-medium">
                  {t("Only")} {selectedVariation.stock}{" "}
                  {t("items left in stock")}
                </span>
              </div>
            )}

            {/* PRICE */}
            <div className="bg-white rounded-lg p-6 shadow-md space-y-4">
              <div className="text-3xl font-bold text-gray-900">
                <Price amount={Number(getCurrentPrice())} />
              </div>
              <div className="text-green-600 font-semibold">
                <span
                  className={`px-2 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${getStockStatusColor(product.stockStatus)}`}
                >
                  {
                    stockStatuses.find((s) => s.value === product.stockStatus)
                      ?.label
                  }
                </span>
              </div>
              {/* <p className="text-sm text-gray-500">
                {t("Delivered in 3–5 business days")}
              </p> */}

              <div className="flex items-center gap-2">
                {showRating && (
                  <ProductRating
                    product={product}
                    mode={canEditProduct ? "full" : "compact"}
                    showReviews={true}
                    allowRating={!canEditProduct}
                    onRatingUpdate={(rating) => {
                      console.info("New rating:", rating);
                    }}
                  />
                )}
                <span className="text-gray-600 font-medium">
                  {product.rating}
                </span>
              </div>
            </div>

            {/* ACTION BUTTONS */}
            {user?.role !== "admin" && (
              <div className="sticky bottom-0 bg-white rounded-lg p-6 shadow-md flex gap-4">
                <AddToCartButton
                  productId={product.id}
                  variationId={selectedVariation?.id}
                  className="w-full"
                  disabled={shouldDisableAddToCart}
                  // disabled={
                  //   variationsLoading ||
                  //   isOutOfStock ||
                  //   (!!product.variations?.length && !selectedVariation)
                  // }
                  onSuccess={() => console.log("Added to cart!")}
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFavorite(product.id);
                  }}
                  disabled={togglingFavorite}
                  className={`p-4 border-2 border-gray-300 hover:border-red-500 rounded-lg transition-all ${
                    togglingFavorite ? "scale-95 opacity-70" : "hover:scale-105"
                  } ${isFavorite ? "border-red-500 bg-red-50" : ""}`}
                >
                  <Heart
                    size={24}
                    fill={isFavorite ? FAVORITE : "none"}
                    color={isFavorite ? FAVORITE : "#6B7280"}
                    className={togglingFavorite ? "animate-pulse" : ""}
                  />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* UPDATE IMAGES MODAL */}
      {showImageModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
          onClick={() => setShowImageModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-xl font-bold text-gray-900">
                {t("Update Product Images")}
              </h3>
              <button
                className="text-gray-500 hover:text-gray-800"
                onClick={() => setShowImageModal(false)}
                aria-label={t("Close")}
              >
                <X size={20} />
              </button>
            </div>
            <div className="px-6 py-5 max-h-[60vh] overflow-y-auto">
              <ImageUploader
                value={editImages}
                onChange={(val) =>
                  setEditImages(Array.isArray(val) ? val : [val])
                }
                multiple={true}
                label=""
              />
            </div>
            <div className="px-6 py-4 border-t flex items-center justify-end gap-3">
              <button
                type="button"
                className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200"
                onClick={() => setShowImageModal(false)}
              >
                {t("Cancel")}
              </button>
              <button
                type="button"
                disabled={imageSaving}
                className="px-5 py-2 rounded-lg bg-black text-white font-semibold hover:bg-gray-700 disabled:opacity-60 transition-colors"
                onClick={async () => {
                  if (!productId) return;
                  try {
                    setImageSaving(true);
                    const res = await BaseApi.put(`/products/${productId}`, {
                      images: editImages,
                    });
                    if (!res?.success)
                      throw new Error("Failed to update images");
                    setProduct((prev) => ({
                      ...prev,
                      images: res?.data?.images || editImages,
                    }));
                    setShowImageModal(false);
                    success(t("Product images updated successfully"));
                  } catch (error) {
                    console.error("Error updating images:", error);
                    err(t("Failed to update product images"));
                  } finally {
                    setImageSaving(false);
                  }
                }}
              >
                {imageSaving ? t("Saving...") : t("Save Images")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SIZE GUIDE MODAL */}
      {showSizeGuide && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          onClick={() => setShowSizeGuide(false)}
        >
          <div
            className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between">
              <h3 className="text-xl font-semibold">Size Guide</h3>
              <button
                className="p-2 hover:bg-gray-100 rounded-full"
                onClick={() => setShowSizeGuide(false)}
              >
                <ChevronLeft size={20} />
              </button>
            </div>
            <div className="p-4">
              <img
                src="https://sciconsports.com/cdn/shop/files/size-chart-1.webp?v=1728040083&width=1400"
                alt="Size Guide"
                className="w-full rounded-lg"
              />
            </div>
          </div>
        </div>
      )}

      {/* VARIATION MODAL */}
      <VariationModal
        isOpen={showVariationModal}
        onClose={() => {
          setShowVariationModal(false);
          setEditingVariation(null);
        }}
        onSave={handleSaveVariation}
        productId={productId!}
        productPrice={product.price}
        variation={editingVariation}
      />
    </div>
  );
};

export default ProductDetails;
